// Persistence layer for WASM-backed devices.
//
// Parallel to the legacy `devices`/`keyrings` stores in
// src/crypto/indexeddb.secure.ts — intentionally kept separate so the
// legacy flow remains intact for rollback while the WASM migration is
// in progress. The legacy stores get retired only once the WASM
// useDevice swap is fully shipped.
//
// Each browser is its own device, with its own seed, master key, and
// enrolled authenticators. A device can be unlocked via:
//   - a passphrase (optional; Argon2id + AES-GCM wrapped envelope), and/or
//   - one or more registered passkeys (WebAuthn PRF-wrapped envelopes).
// At least one unlock method must exist at all times — EXCEPT for an
// anonymous device created silently such as by the /ava landing (2026-09,
// docs/Ava-Anonymous-Landing.md in practicemd-api): it carries its master
// key in the clear in `unprotectedMasterKeyHex` so Ava can speak before
// the visitor is asked to protect anything. Such a device is PROMOTED
// (passphrase and/or passkey wrap the same master key; the clear copy is
// dropped) before it is ever bound to an account. The unlock UI presents
// whichever methods are available and lets the user pick.

import { openDB, IDBPDatabase } from 'idb'

const DB_NAME = 'practicemd-wasm-device'
// v2 added two-envelope shape; v3 added authenticators[] + optional
// passphrase; v4 adds keyringEnvelopeB64 (master-key-wrapped Go
// ClientKeyring). v2 → v4 is non-destructive — missing fields are
// normalized to sane defaults on read.
const DB_VERSION = 4
const STORE = 'devices'
const META_STORE = 'meta'
const CURRENT_KEY = 'currentSeedKid'

export interface StoredAuthenticator {
    // Base64url WebAuthn credential ID. Used as the allowCredentials
    // entry during the unlock ceremony and as the key for removal.
    credentialId: string
    // AES-GCM wrap of the master key under the authenticator's PRF
    // output. Same envelope format as seedEnvelopeB64 (direct AES-GCM,
    // no KDF) — the PRF output is already a uniformly random 32-byte
    // key so no key derivation is needed.
    envelopeB64: string
    // Hints that the unlock UI can pass back to navigator.credentials.get
    // so the browser knows where to look for this passkey.
    transports?: string[]
    // User-visible label, e.g. "Touch ID on laptop". Optional — the
    // harness leaves it empty for now.
    label?: string
    createdAt: number
}

export interface WasmDeviceRecord {
    seedKid: string
    sigKid: string
    encKid: string
    aesKid: string
    // Always present: wraps the 32-byte seed under the master key.
    seedEnvelopeB64: string
    // Optional: present only when the user has chosen to enable
    // passphrase unlock. A device can be passkey-only.
    passphraseEnvelopeB64?: string
    // One entry per registered passkey on this browser. Empty array
    // means no passkeys enrolled yet (passphrase-only mode).
    authenticators: StoredAuthenticator[]
    // Master-key-wrapped exported Go ClientKeyring (base64 JSON of
    // pxkDistributions + distributedKeys). Populated by pullKeyring;
    // undefined for devices that haven't pulled yet.
    keyringEnvelopeB64?: string
    // Present ONLY on an unprotected anonymous device (see header): the
    // master key itself, hex. Anything with access to this browser
    // profile's IndexedDB can decrypt this device's data while it is set.
    // Removed by promoteWasmDevice once a passphrase/passkey wraps it.
    unprotectedMasterKeyHex?: string
    createdAt: number
    label?: string
}

function assertBrowser() {
    if (typeof indexedDB === 'undefined') {
        throw new Error('deviceStore: IndexedDB is not available in this environment')
    }
}

async function open(): Promise<IDBPDatabase> {
    assertBrowser()
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
            // v1 → v2 changed the envelope shape; v2 → v3 adds fields.
            // v2 records would be missing `authenticators` entirely.
            // We give them an empty array on the fly below — no destructive
            // drop needed for the v2 → v3 step.
            if (oldVersion < 2) {
                if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE)
                if (db.objectStoreNames.contains(META_STORE)) db.deleteObjectStore(META_STORE)
            }
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'seedKid' })
            }
            if (!db.objectStoreNames.contains(META_STORE)) {
                db.createObjectStore(META_STORE)
            }
        },
    })
}

// v2 records predate the authenticators[] field. Normalize on read so
// callers can trust the array exists.
function normalize(record: any): WasmDeviceRecord {
    if (!Array.isArray(record.authenticators)) record.authenticators = []
    return record as WasmDeviceRecord
}

// Event dispatched on the window after a successful saveWasmDevice.
// useDevice's init listens for this so its React state (seedKid /
// sigKid / encKid / nickname) refreshes right after the lazy setup
// prompter resolves — without it the seedKid-gated heartbeat
// wouldn't start until the next page load.
export const WASM_DEVICE_SAVED_EVENT = 'practicemd-wasm-device-saved'

export async function saveWasmDevice(record: WasmDeviceRecord): Promise<void> {
    const db = await open()
    const tx = db.transaction([STORE, META_STORE], 'readwrite')
    await tx.objectStore(STORE).put(record)
    await tx.objectStore(META_STORE).put(record.seedKid, CURRENT_KEY)
    await tx.done
    db.close()
    if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event(WASM_DEVICE_SAVED_EVENT)) } catch { /* non-fatal */ }
    }
}

export async function loadCurrentWasmDevice(): Promise<WasmDeviceRecord | null> {
    const db = await open()
    try {
        const current = (await db.get(META_STORE, CURRENT_KEY)) as string | undefined
        if (!current) return null
        const record = await db.get(STORE, current)
        return record ? normalize(record) : null
    } finally {
        db.close()
    }
}

export async function hasWasmDevice(): Promise<boolean> {
    return (await loadCurrentWasmDevice()) !== null
}

// A device is protected when at least one unlock method wraps its master
// key. An unprotected device is the anonymous-landing kind and must be
// promoted before account-binding ops (see prepareDeviceArgs' requireProtected).
export function isDeviceProtected(record: WasmDeviceRecord): boolean {
    return !!record.passphraseEnvelopeB64 || record.authenticators.length > 0
}

// Promote an unprotected device: install the given unlock method(s) and
// drop the clear master key, atomically in one transaction. Fires the
// saved event like saveWasmDevice so the provider refreshes.
export async function promoteWasmDevice(
    seedKid: string,
    protection: { passphraseEnvelopeB64?: string; authenticators?: StoredAuthenticator[] },
): Promise<WasmDeviceRecord> {
    if (!protection.passphraseEnvelopeB64 && !(protection.authenticators?.length)) {
        throw new Error('promoteWasmDevice: at least one unlock method is required')
    }
    const db = await open()
    let updated: WasmDeviceRecord
    try {
        const tx = db.transaction(STORE, 'readwrite')
        const existing = (await tx.store.get(seedKid)) as WasmDeviceRecord | undefined
        if (!existing) throw new Error(`no device with seedKid ${seedKid}`)
        if (protection.passphraseEnvelopeB64) existing.passphraseEnvelopeB64 = protection.passphraseEnvelopeB64
        if (protection.authenticators?.length) {
            existing.authenticators = [...(existing.authenticators || []), ...protection.authenticators]
        }
        delete existing.unprotectedMasterKeyHex
        await tx.store.put(existing)
        await tx.done
        updated = normalize(existing)
    } finally {
        db.close()
    }
    if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event(WASM_DEVICE_SAVED_EVENT)) } catch { /* non-fatal */ }
    }
    return updated
}

// Replace the current device's authenticator list. Used by add/remove
// flows. A device with zero authenticators AND no passphrase envelope
// would be unreachable, but enforcing that invariant is the caller's
// job — this layer stays a dumb persistence store.
export async function updateAuthenticators(
    seedKid: string,
    authenticators: StoredAuthenticator[]
): Promise<void> {
    const db = await open()
    try {
        const tx = db.transaction(STORE, 'readwrite')
        const existing = (await tx.store.get(seedKid)) as WasmDeviceRecord | undefined
        if (!existing) throw new Error(`no device with seedKid ${seedKid}`)
        existing.authenticators = authenticators
        await tx.store.put(existing)
        await tx.done
    } finally {
        db.close()
    }
}

// Replace the passphrase envelope on the current device (add, change,
// or drop). Pass `undefined` to remove the passphrase envelope.
export async function updatePassphraseEnvelope(
    seedKid: string,
    passphraseEnvelopeB64: string | undefined
): Promise<void> {
    const db = await open()
    try {
        const tx = db.transaction(STORE, 'readwrite')
        const existing = (await tx.store.get(seedKid)) as WasmDeviceRecord | undefined
        if (!existing) throw new Error(`no device with seedKid ${seedKid}`)
        if (passphraseEnvelopeB64) existing.passphraseEnvelopeB64 = passphraseEnvelopeB64
        else delete existing.passphraseEnvelopeB64
        await tx.store.put(existing)
        await tx.done
    } finally {
        db.close()
    }
}

// Replace the (master-key-wrapped) keyring envelope on the current
// device. Called after pullKeyring and any op that grew the keyring.
export async function updateKeyringEnvelope(
    seedKid: string,
    keyringEnvelopeB64: string
): Promise<void> {
    const db = await open()
    try {
        const tx = db.transaction(STORE, 'readwrite')
        const existing = (await tx.store.get(seedKid)) as WasmDeviceRecord | undefined
        if (!existing) throw new Error(`no device with seedKid ${seedKid}`)
        existing.keyringEnvelopeB64 = keyringEnvelopeB64
        await tx.store.put(existing)
        await tx.done
    } finally {
        db.close()
    }
}

export async function clearWasmDevice(): Promise<void> {
    const db = await open()
    try {
        const current = (await db.get(META_STORE, CURRENT_KEY)) as string | undefined
        if (!current) return
        const tx = db.transaction([STORE, META_STORE], 'readwrite')
        await tx.objectStore(STORE).delete(current)
        await tx.objectStore(META_STORE).delete(CURRENT_KEY)
        await tx.done
    } finally {
        db.close()
    }
}
